import { useEffect, useState } from "react";
import { ExternalLink, FileText, Package } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  BucketContents,
  BucketContentEstimateRow,
  BucketContentBookedOrderRow,
} from "@shared-core/data/adapter";
import { Card } from "../../primitives/card";

// "What's in this bucket" — the deep-dive replacement for the bucket-page
// chart + adjustment grid. Adjustments now live inline on the rep page; the
// bucket page is the place users come to see the actual work
// (forecast_categorisation_row_assignments) flowing into the bucket.
//
// Lists are capped server-side at 50; the backend returns counts so we can
// show "showing 50 of 312" when the bucket is bigger than the slice.

function fmtCurrency(amt: number): string {
  if (amt === 0) return "—";
  const sign = amt < 0 ? "-" : "";
  const abs = Math.abs(amt);
  if (abs >= 1_000_000) return `${sign}£${(abs / 1_000_000).toFixed(2)}m`;
  if (abs >= 1_000) return `${sign}£${Math.round(abs / 1_000)}k`;
  return `${sign}£${Math.round(abs)}`;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  // Inputs come from Python as YYYY-MM-DD; we just render them. No
  // timezone juggling — these are calendar dates, not instants.
  return iso.slice(0, 10);
}

interface Props {
  bucketId: string;
}

export function BucketContentsPanel({ bucketId }: Props) {
  const adapter = useDataAdapter();
  const [data, setData] = useState<BucketContents | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    (async () => {
      try {
        const result = await adapter.getBucketContents(bucketId);
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adapter, bucketId]);

  if (loading) {
    return (
      <Card className="p-6 text-sm text-muted-foreground">
        Loading bucket contents…
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-4 border-red-200 bg-red-50/50 text-sm text-red-900">
        Couldn't load bucket contents: {error}
      </Card>
    );
  }

  if (!data) return null;

  const totalRows = data.estimatesCount + data.bookedOrdersCount;

  return (
    <Card>
      <div className="p-3 border-b border-border flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">What's in this bucket</h3>
          <p className="text-xs text-muted-foreground">
            Recent estimates and booked orders the categorisation pipeline has
            assigned to this bucket.
          </p>
        </div>
        <div className="text-xs text-muted-foreground tabular-nums shrink-0">
          {totalRows} row{totalRows === 1 ? "" : "s"} total
        </div>
      </div>

      <div className="grid gap-4 p-3 md:grid-cols-2">
        <EstimatesSection
          estimates={data.estimates}
          count={data.estimatesCount}
        />
        <BookedOrdersSection
          orders={data.bookedOrders}
          count={data.bookedOrdersCount}
        />
      </div>
    </Card>
  );
}

// ── Estimates section ────────────────────────────────────────────────

function EstimatesSection({
  estimates,
  count,
}: {
  estimates: BucketContentEstimateRow[];
  count: number;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <h4 className="text-xs font-semibold uppercase text-muted-foreground flex items-center gap-1">
          <FileText className="h-3.5 w-3.5" />
          Estimates
        </h4>
        <span className="text-xs text-muted-foreground tabular-nums">
          {count === 0
            ? "none"
            : count > estimates.length
              ? `showing ${estimates.length} of ${count}`
              : `${count}`}
        </span>
      </div>
      {estimates.length === 0 ? (
        <p className="text-xs italic text-muted-foreground p-2">
          No estimates assigned.
        </p>
      ) : (
        <div className="overflow-hidden rounded border border-border">
          <table className="w-full text-xs">
            <thead className="bg-surface-hover">
              <tr>
                <th className="text-left p-1.5 font-medium">Date</th>
                <th className="text-left p-1.5 font-medium">Customer</th>
                <th className="text-right p-1.5 font-medium">Value</th>
                <th className="text-right p-1.5 font-medium">Contrib</th>
                <th className="text-left p-1.5 font-medium w-6"></th>
              </tr>
            </thead>
            <tbody>
              {estimates.map((e) => (
                <tr
                  key={e.id}
                  className="border-t border-border hover:bg-surface-hover/30"
                >
                  <td className="p-1.5 tabular-nums whitespace-nowrap">
                    {fmtDate(e.estimateDate)}
                  </td>
                  <td
                    className="p-1.5 truncate max-w-[14rem]"
                    title={e.customer?.name ?? ""}
                  >
                    {e.customer?.name ?? (
                      <span className="text-muted-foreground italic">
                        (no customer)
                      </span>
                    )}
                    {e.description && (
                      <div
                        className="text-[10px] text-muted-foreground truncate"
                        title={e.description}
                      >
                        {e.description}
                      </div>
                    )}
                  </td>
                  <td className="p-1.5 text-right tabular-nums">
                    {fmtCurrency(e.estimateAmt)}
                  </td>
                  <td className="p-1.5 text-right tabular-nums text-muted-foreground">
                    {fmtCurrency(e.contributionAmt)}
                  </td>
                  <td className="p-1.5">
                    <a
                      href={e.selfUrl}
                      target="_blank"
                      rel="noreferrer"
                      title="Open estimate"
                      className="inline-flex text-muted-foreground hover:text-primary"
                    >
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Booked orders section ────────────────────────────────────────────

function BookedOrdersSection({
  orders,
  count,
}: {
  orders: BucketContentBookedOrderRow[];
  count: number;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <h4 className="text-xs font-semibold uppercase text-muted-foreground flex items-center gap-1">
          <Package className="h-3.5 w-3.5" />
          Booked orders
        </h4>
        <span className="text-xs text-muted-foreground tabular-nums">
          {count === 0
            ? "none"
            : count > orders.length
              ? `showing ${orders.length} of ${count}`
              : `${count}`}
        </span>
      </div>
      {orders.length === 0 ? (
        <p className="text-xs italic text-muted-foreground p-2">
          No booked orders assigned.
        </p>
      ) : (
        <div className="overflow-hidden rounded border border-border">
          <table className="w-full text-xs">
            <thead className="bg-surface-hover">
              <tr>
                <th className="text-left p-1.5 font-medium">Booked</th>
                <th className="text-left p-1.5 font-medium">Customer</th>
                <th className="text-right p-1.5 font-medium">Value</th>
                <th className="text-right p-1.5 font-medium">Contrib</th>
                <th className="text-left p-1.5 font-medium w-6"></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr
                  key={o.id}
                  className="border-t border-border hover:bg-surface-hover/30"
                >
                  <td className="p-1.5 tabular-nums whitespace-nowrap">
                    {fmtDate(o.orderBookingDate)}
                    {o.invoicedDate && (
                      <div className="text-[10px] text-muted-foreground">
                        inv {fmtDate(o.invoicedDate)}
                      </div>
                    )}
                  </td>
                  <td
                    className="p-1.5 truncate max-w-[14rem]"
                    title={o.customer?.name ?? ""}
                  >
                    {o.customer?.name ?? (
                      <span className="text-muted-foreground italic">
                        (no customer)
                      </span>
                    )}
                    {o.sourceSystemTharsternJobNumber && (
                      <div className="text-[10px] text-muted-foreground">
                        job {o.sourceSystemTharsternJobNumber}
                      </div>
                    )}
                  </td>
                  <td className="p-1.5 text-right tabular-nums">
                    {fmtCurrency(o.bookingAmt)}
                  </td>
                  <td className="p-1.5 text-right tabular-nums text-muted-foreground">
                    {fmtCurrency(o.contributionAmt)}
                  </td>
                  <td className="p-1.5">
                    <a
                      href={o.selfUrl}
                      target="_blank"
                      rel="noreferrer"
                      title="Open booked order"
                      className="inline-flex text-muted-foreground hover:text-primary"
                    >
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
